<template>
  <div class="space-y-6 h-auto bg-white px-8 text-grayDark rounded">
    <div class="mb-7 space-y-6 py-1 text-[14px] rounded-[6px] items-center">
      <div class="gap-4 items-center">
        <div class="flex gap-4">
          <p class="flex items-center gap-2 rounded-[6px]">
            <img src="@/assets/projects/filter.svg" alt="Icon" class="" />
            Filters
          </p>

          <!-- View Toggle Tabs -->
          <div class="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
            <button
              @click="activeView = 'current'"
              :class="[
                'px-3 py-1 rounded-md text-sm transition-colors',
                activeView === 'current' 
                  ? 'bg-white text-primaryDark shadow-sm' 
                  : 'text-grayDark hover:text-primaryDark'
              ]"
            >
              Current
            </button>
            <button
              @click="activeView = 'history'"
              :class="[
                'px-3 py-1 rounded-md text-sm transition-colors flex items-center gap-2',
                activeView === 'history' 
                  ? 'bg-white text-primaryDark shadow-sm' 
                  : 'text-grayDark hover:text-primaryDark'
              ]"
            >
              <img src="@/assets/history.svg" alt="History" class="w-4 h-4" />
              History
            </button>
          </div>

          <div v-if="activeView === 'current'" class="flex items-center gap-4">
            <!-- Talent Filter -->
            <div ref="dropdown" @click="stopEventPropagation">
              <div
                class="flex items-center gap-2 px-2 py-1 border border-grayLight rounded cursor-pointer"
                @click="openModal"
              >
                {{ selectedTalent || "Talent" }}
              </div>
            </div>

            <div>
              <!-- Button to open the time picker -->
              <!-- <button
                @click="showTimeRangePicker = true"
                class="bg- text-grayDark border border-grayLight px-2 py-1 rounded flex items-center gap-2"
              >
                {{ formattedTimeRange || "Date" }}
              </button> -->

              <!-- Time Range Picker Modal -->
              <!-- <TimeRangePicker
                :isOpen="showTimeRangePicker"
                @close="showTimeRangePicker = false"
                @update-time="handleTimeRange"
              /> -->

              <!-- <button @click="showDateFilter = true" class="px-3 py-1 border rounded-[8px] flex items-center"
              >
              <span v-if="!hasDateFilter">Date</span>
              <span v-else class="text-bodyText">Date range
                <span class="text-grayDark">
                  {{ formatDateForDisplay(startDate) }} {{ endDate ? '- ' + formatDateForDisplay(endDate) : '' }}

                </span> 
              </span>
            </button> -->

              <!-- Date filter modal -->
              <!-- <DateRangePicker :isOpen="showDateFilter" :initialStartDate="startDate" :initialEndDate="endDate"
              @close="showDateFilter = false" @apply="applyDateFilter" /> -->

              <!-- Inside template -->
              <button
                @click="showDateFilter = true"
                class="px-3 py-1 border rounded-[8px] flex items-center"
              >
                <span v-if="!hasDateFilter">Date</span>
                <span v-else class="text-bodyText">
                  {{ formatDateForDisplay(startDate) }} -
                  {{ formatDateForDisplay(endDate) }}
                </span>
              </button>

              <ActivityDateFilter
                :isOpen="showDateFilter"
                :initialStartDate="startDate"
                :initialEndDate="endDate"
                @close="showDateFilter = false"
                @apply="applyDateFilter"
              />
            </div>
          </div>
        </div>

        <!-- Success Message -->
        <div
          v-if="successMessage"
          class="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mt-4"
        >
          <span class="block sm:inline">{{ successMessage }}</span>
        </div>

        <!-- History View -->
        <div v-if="activeView === 'history'" class="mt-8">
          <!-- History Header -->
          <div class="flex items-center justify-between mb-6">
            <div>
              <h3 class="text-lg font-medium">AI Summary History</h3>
              <p class="text-sm text-grayDark">View previously generated summaries</p>
            </div>
            <div class="flex items-center gap-3">
              <!-- Optional: Add filter by talent -->
              <select
                v-model="historyFilters.talent_id"
                @change="currentHistoryPage = 1; loadHistory()"
                class="px-3 py-1 border rounded text-sm"
              >
                <option value="">All Talents</option>
                <option
                  v-for="member in members"
                  :key="member.talent_id"
                  :value="member.talent_id"
                >
                  {{ capitalize(member.talent_first_name) }} {{ capitalize(member.talent_last_name) }}
                </option>
              </select>
              
            </div>
          </div>

          <!-- History List -->
          <div v-if="isLoadingHistory" class="flex items-center justify-center py-12">
            <div class="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primaryDark"></div>
            <span class="ml-3 text-grayDark">Loading history...</span>
          </div>

          <div v-else-if="historyError" class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
            <span class="block sm:inline">{{ historyError }}</span>
            <button @click="loadHistory" class="ml-4 text-sm underline hover:no-underline">
              Try Again
            </button>
          </div>

          <div v-else-if="summaryHistory.length > 0" class="space-y-4">
            <div
              v-for="historyItem in paginatedHistory"
              :key="historyItem.id"
              class="border border-grayLight rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
              @click="viewHistoryItem(historyItem)"
            >
              <div class="flex items-start justify-between">
                <div class="flex-1">
                  <div class="flex items-center gap-3 mb-2">
                    <div
                      class="w-8 h-8 text-xs flex items-center justify-center rounded-full text-white font-semibold bg-[#A3DB7D]"
                    >
                      {{ getTalentInitialsFromHistoryItem(historyItem) }}
                    </div>
                    <div class="flex-1">
                      <div class="flex items-center gap-2">
                        <p class="font-medium text-gray-900">{{ getTalentNameFromHistoryItem(historyItem) }}</p>
                        <!-- Status indicator -->
                        <span 
                          :class="{
                            'bg-green-100 text-green-800': historyItem.status === 'completed',
                            'bg-blue-100 text-blue-800': historyItem.status === 'processing',
                            'bg-red-100 text-red-800': historyItem.status === 'failed',
                            'bg-gray-100 text-gray-800': !['completed', 'processing', 'failed'].includes(historyItem.status)
                          }"
                          class="px-2 py-1 rounded-full text-xs font-medium capitalize"
                        >
                          {{ historyItem.status || 'unknown' }}
                        </span>
                      </div>
                      <p class="text-xs text-grayDark">{{ formatHistoryDate(historyItem) }}</p>
                    </div>
                  </div>
                  <p class="text-sm text-grayDark line-clamp-2">
                    {{ getPreviewText(historyItem) }}
                  </p>
                </div>
                <div class="flex items-center gap-2 ml-4">
                  <span class="text-xs text-grayDark">{{ formatTimestamp(historyItem.created_at) }}</span>
                  <button
                    @click.stop="deleteHistoryItem(historyItem.id)"
                    class="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50"
                  >
                    <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            <!-- Pagination for History -->
            <div v-if="totalHistoryItems > historyPageSize" class="flex items-center justify-between pt-4">
              <p class="text-sm text-grayDark">
                Showing {{ ((currentHistoryPage - 1) * historyPageSize) + 1 }} to {{ Math.min(currentHistoryPage * historyPageSize, totalHistoryItems) }} of {{ totalHistoryItems }} results
              </p>
              <div class="flex items-center gap-2">
                <button
                  @click="currentHistoryPage--"
                  :disabled="currentHistoryPage === 1 || isLoadingHistory"
                  class="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Previous
                </button>
                <span class="px-3 py-1 text-sm">{{ currentHistoryPage }} of {{ totalHistoryPages }}</span>
                <button
                  @click="currentHistoryPage++"
                  :disabled="currentHistoryPage >= totalHistoryPages || isLoadingHistory"
                  class="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Next
                </button>
              </div>
            </div>
          </div>

          <!-- Empty History State -->
          <div v-else-if="!isLoadingHistory && !historyError" class="flex items-center justify-center mt-24">
            <div class="px-6 py-4 text-center gap-2 border border-[#EEEEEE] justify-center align-center w-[360px] rounded-[4px]">
              <img src="@/assets/history.svg" alt="History" class="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p class="font-semibold text-[20px] mb-2">No History Available</p>
              <p class="text-grayDark">Generate AI summaries to see them appear here</p>
            </div>
          </div>
        </div>

        <!-- Current View Content (existing content) -->
        <div v-if="activeView === 'current'">
        <!-- Status Message -->
        <div
          v-if="aiStatus && !queryAttempted"
          class="bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded relative mt-4"
        >
          <span class="block sm:inline">{{ aiStatus }}</span>
        </div>

        <!-- Loading State -->
        <div v-if="isLoading" class="flex items-center justify-center mt-16">
          <div
            class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primaryDark"
          ></div>
        </div>

        <!-- Empty State (Before Query) -->
        <!-- <div v-else-if="!aiResponse && !queryAttempted && !errorMessage" class="flex items-center justify-center mt-24"> -->
        <div
          v-else-if="!queryAttempted"
          class="flex items-center justify-center mt-24"
        >
          <div
            class="px-6 py-4 text-center gap-2 border border-[#EEEEEE] justify-center align-center w-[360px] rounded-[4px]"
          >
            <p class="font-semibold text-[20px]">Use AI to Generate Summary</p>
            <p>
              Get instant Summary of work done by Talents by Query Talent
              assigned Task and Time range
            </p>
            <button
              @click="sendToAICopilot"
              class="mt-4 flex items-center justify-center gap-2 px-4 bg-primaryDark text-primaryLight rounded-[6px] py-2 cursor-pointer w-full"
              :class="{ 'opacity-50 cursor-not-allowed': !canQuery }"
              :disabled="!canQuery"
            >
              <img
                src="@/assets/projects/cardano-(ada).svg"
                alt="Icon"
                class="w-4 h-4"
              />
              Generate Summary
            </button>
          </div>
        </div>

        <!-- Results Section -->
        <div
          v-if="(aiResponse || errorMessage) && queryAttempted && !isLoading"
          class="mt-8"
        >
          <!-- Activity List -->
          <div class="space-y-4">
            <div class="flex flex-col justify-between items-end">
              <div class="bg-gray-50 p-2 rounded-lg mb-6 align-right w-[200px]">
                <p class="text-[10px] font-bold">{{ selectedTalent }}</p>
                <p class="text-[11px]">{{ formattedTimeRange }}</p>
              </div>
            </div>

            <h3 class="text-lg font-medium mb-4 align-center text-center">
              Generated Report for {{ selectedTalent }}
            </h3>

            <div class="flex gap-2 items-center">
              <p class="w-[90px]">{{ month }}</p>
              <div class="w-full h-[1px] border border-grayLight"></div>
            </div>

            <div
              class="text-gray-700 whitespace-pre-line p-4 border border-grayLight rounded mt-4"
            >
              <div
                v-if="containsHTML(aiResponse)"
                v-html="sanitizeHTML(aiResponse)"
              ></div>
              <div v-else>{{ aiResponse || errorMessage }}</div>
            </div>
          </div>
        </div>
        </div>
      </div>
    </div>

    <!-- History Item Detail Modal -->
    <Teleport to="body">
      <div
        v-if="selectedHistoryItem"
        class="fixed inset-0 flex items-center justify-center z-50"
      >
        <!-- Backdrop -->
        <div
          class="absolute inset-0 bg-black bg-opacity-50"
          @click="closeHistoryDetail"
        ></div>

        <!-- Modal -->
        <div
          class="bg-white max-w-4xl w-full max-h-[90vh] p-6 rounded-lg shadow-lg relative z-50 flex flex-col"
        >
          <!-- Header -->
          <div
            class="flex justify-between items-center border-b pb-3 sticky top-0 bg-white z-10"
          >
            <div>
              <h3 class="text-lg font-medium">AI Summary Details</h3>
              <p class="text-sm text-grayDark">{{ getTalentNameFromHistoryItem(selectedHistoryItem) }} - {{ formatHistoryDate(selectedHistoryItem) }}</p>
            </div>
            <button
              @click="closeHistoryDetail"
              class="h-8 w-8 rounded-full flex items-center justify-center bg-gray-100"
            >
              <img
                src="@/assets/tasks/iconoir_cancel.svg"
                alt="Close"
                class="h-5 w-5"
              />
            </button>
          </div>

          <!-- Content -->
          <div class="py-4 overflow-y-auto flex-1">
            <div class="space-y-4">
              <!-- Summary Info -->
              <div class="bg-gray-50 p-4 rounded-lg">
                <div class="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p class="font-medium text-grayDark">Talent:</p>
                    <p>{{ getTalentNameFromHistoryItem(selectedHistoryItem) }}</p>
                  </div>
                  <div>
                    <p class="font-medium text-grayDark">Date Range:</p>
                    <p>{{ formatHistoryDate(selectedHistoryItem) }}</p>
                  </div>
                  <div>
                    <p class="font-medium text-grayDark">Generated:</p>
                    <p>{{ formatTimestamp(selectedHistoryItem.created_at) }}</p>
                  </div>
                  <div>
                    <p class="font-medium text-grayDark">Project:</p>
                    <p>{{ selectedHistoryItem.project_name || projectName }}</p>
                  </div>
                  <div>
                    <p class="font-medium text-grayDark">Status:</p>
                    <p class="capitalize">{{ selectedHistoryItem.status }}</p>
                  </div>
                  <div>
                    <p class="font-medium text-grayDark">Requested by:</p>
                    <p>{{ selectedHistoryItem.requested_by }}</p>
                  </div>
                </div>
              </div>

              <!-- Summary Content -->
              <div class="border border-grayLight rounded-lg p-4">
                <h4 class="font-medium mb-3">Summary Status:</h4>
                
                <div v-if="selectedHistoryItem.status === 'completed' && selectedHistoryItem.has_response">
                  <div class="bg-green-50 border border-green-200 rounded p-3 mb-3">
                    <p class="text-green-700">✓ Summary completed successfully</p>
                  </div>
                  <p class="text-sm text-grayDark mb-3">
                    The AI summary has been generated for this date range. 
                    <em>Note: Full summary content would be fetched from a separate API endpoint.</em>
                  </p>
                  
                  <div class="bg-gray-50 p-3 rounded border-l-4 border-blue-500">
                    <p class="text-sm text-grayDark italic">
                      {{ sanitizeHTML(selectedHistoryItem.response) || 'No summary available' }}
                    </p>
                  </div>
                </div>
                
                <div v-else-if="selectedHistoryItem.status === 'processing'" class="bg-blue-50 border border-blue-200 rounded p-3">
                  <p class="text-blue-700">⏳ Summary is being processed...</p>
                </div>
                
                <div v-else-if="selectedHistoryItem.status === 'failed'" class="bg-red-50 border border-red-200 rounded p-3">
                  <p class="text-red-700">❌ Summary generation failed</p>
                  <p v-if="selectedHistoryItem.error_message" class="text-sm text-red-600 mt-2">
                    Error: {{ selectedHistoryItem.error_message }}
                  </p>
                </div>
                
                <div v-else class="bg-yellow-50 border border-yellow-200 rounded p-3">
                  <p class="text-yellow-700">Status: {{ selectedHistoryItem.status || 'Unknown' }}</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Footer Actions -->
          <div class="flex justify-between items-center pt-4 border-t">
            <button
              @click="deleteHistoryItem(selectedHistoryItem.id); closeHistoryDetail()"
              class="text-red-600 hover:text-red-800 px-3 py-2 border border-red-200 rounded hover:bg-red-50 transition-colors"
            >
              Delete from History
            </button>
            <button
              @click="closeHistoryDetail"
              class="px-4 py-2 bg-primaryDark text-white rounded-lg hover:bg-opacity-90 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Talent Selection Modal -->
    <Teleport to="body">
      <div
        v-if="isModalOpen"
        class="fixed inset-0 flex items-center justify-center z-50"
      >
        <!-- Backdrop -->
        <div
          class="absolute inset-0 bg-black bg-opacity-50"
          @click="closeModal"
        ></div>

        <!-- Modal -->
        <div
          class="bg-white max-w-lg w-full max-h-[90vh] p-6 rounded-lg shadow-lg relative z-50 flex flex-col"
        >
          <!-- Header -->
          <div
            class="flex justify-between items-center border-b pb-3 sticky top-0 bg-white z-10"
          >
            <h3 class="text-lg font-medium">Search Talent Name</h3>
            <button
              @click="closeModal"
              class="h-8 w-8 rounded-full flex items-center justify-center bg-gray-100"
            >
              <img
                src="@/assets/tasks/iconoir_cancel.svg"
                alt="Close"
                class="h-5 w-5"
              />
            </button>
          </div>

          <!-- Search Input -->
          <div class="relative mt-4 sticky top-[50px] bg-white z-10">
            <input
              v-model="searchQuery"
              type="text"
              class="w-full h-11 px-3 pl-10 rounded-md shadow-sm bg-gray-100 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              placeholder="Enter a keyword"
              required
            />
            <button
              class="absolute top-1/2 left-3 transform -translate-y-1/2 cursor-pointer"
            >
              <img
                src="@/assets/projects/search-normal.svg"
                alt="Search"
                class="h-5 w-5"
              />
            </button>
          </div>

          <!-- Scrollable Content -->
          <div class="py-4 overflow-y-auto flex-1">
            <div v-if="filteredMembers.length > 0" class="space-y-3">
              <div
                v-for="member in filteredMembers"
                :key="member.project_member_id"
                class="relative"
              >
                <div
                  class="flex gap-3 py-4 whitespace-nowrap cursor-pointer hover:bg-gray-100 p-2 rounded"
                  @click="selectTalent(member)"
                >
                  <div
                    class="w-[42px] h-[42px] text-[14px] flex items-center justify-center rounded-full text-white font-semibold bg-[#A3DB7D]"
                  >
                    {{
                      getInitials(
                        member.talent_first_name,
                        member.talent_last_name
                      )
                    }}
                  </div>

                  <div class="text-[13px]">
                    <p class="font-medium text-gray-900">
                      {{ capitalize(member.talent_first_name) }}
                      {{ capitalize(member.talent_last_name) }}
                    </p>
                    <p class="text-grayDark">{{ member.talent_email }}</p>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="text-center text-gray-500 py-6">
              No talent found in project
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <AISummaryCopilot
      ref="aiSummaryCopilot"
      :projectId="$route.params.projectId"
      :talentId="selectedTalentId"
      @response-received="handleAIResponse"
      @processing="isLoading = true"
      @error="handleAIError"
      @status-update="updateAIStatus"
    />
  </div>
</template>

<script>
import apiClient from "@/axios";
// import TimeRangePicker from "../TimeRangePicker.vue";
import AISummaryCopilot from "../AISummaryCopilot.vue";
import DOMPurify from "dompurify";
import ActivityDateFilter from "../ActivityDateFilter.vue";
// import DateRangePicker from "../DateRangePicker.vue";

export default {
  name: "AISummary",
  components: {
    // TimeRangePicker,
    AISummaryCopilot,
    // DateRangePicker
    ActivityDateFilter,
  },
  props: {
    globalSelectedTalent: {
      type: String,
      default: null,
    },
    globalSelectedTalentId: {
      type: String,
      default: null,
    },
    globalStartDate: {
      type: String,
      default: "",
    },
    globalEndDate: {
      type: String,
      default: "",
    },
  },
  emits: ["update-global-filters"],
  data() {
    return {
      // Existing data properties
      showDateFilter: false,
      startDate: "",
      endDate: "",
      selectedTalent: null,
      selectedTalentId: null,
      isModalOpen: false,
      isLoading: false,
      queryAttempted: false,
      members: [],
      project: {},
      projectName: "",
      searchQuery: "",
      successMessage: "",
      errorMessage: "",
      aiStatus: "",
      month: new Date().toLocaleString("default", {
        month: "long",
        year: "numeric",
      }),
      aiResponse: null,
      
      // New history-related data properties
      activeView: "current", // 'current' or 'history'
      summaryHistory: [], // Array to store history items from API
      selectedHistoryItem: null, // Currently selected history item for detail view
      currentHistoryPage: 1,
      historyPageSize: 10,
      totalHistoryItems: 0, // Total count from API
      currentSummaryId: null, // Track current summary for save status
      isLoadingHistory: false, // Loading state for history
      historyError: null, // Error state for history
      historyFilters: {
        talent_id: null,
        status: null,
        start_date: null,
        end_date: null,
      },
    };
  },

  watch: {
    // Watch for changes in global props and update local state
    globalSelectedTalent: {
      handler(newVal) {
        this.selectedTalent = newVal;
      },
      immediate: true,
    },
    globalSelectedTalentId: {
      handler(newVal) {
        this.selectedTalentId = newVal;
      },
      immediate: true,
    },
    globalStartDate: {
      handler(newVal) {
        this.startDate = newVal;
      },
      immediate: true,
    },
    globalEndDate: {
      handler(newVal) {
        this.endDate = newVal;
      },
      immediate: true,
    },
    
    // Watch for activeView changes to load history when switching to history tab
    activeView: {
      handler(newVal) {
        if (newVal === 'history') {
          this.loadHistory();
        }
      },
    },
    
    // Watch for currentHistoryPage changes to load new page data
    currentHistoryPage: {
      handler() {
        if (this.activeView === 'history') {
          this.loadHistory();
        }
      },
    },
  },

  computed: {
    isAdmin() {
      return this.$store.getters.isAdmin;
    },
    isManager() {
      return this.$store.getters.isManager;
    },
    isTalent() {
      return this.$store.getters.isTalent;
    },
    isInvestor() {
      return this.$store.getters.isInvestor;
    },
    formattedTimeRange() {
      if (!this.startDate || !this.endDate) return "";

      const start = new Date(this.startDate);
      const end = new Date(this.endDate);

      const formatTime = (date) => {
        return date.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        });
      };

      const formatDate = (date) => {
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      };

      return `${formatTime(start)} - ${formatTime(end)} ${formatDate(start)}`;
    },

    canQuery() {
      return this.selectedTalentId && this.startDate && this.endDate;
    },
    filteredMembers() {
      if (!this.searchQuery) return this.members;

      const query = this.searchQuery.toLowerCase();
      return this.members.filter((member) => {
        const fullName =
          `${member.talent_first_name} ${member.talent_last_name}`.toLowerCase();
        const email = member.talent_email.toLowerCase();
        return fullName.includes(query) || email.includes(query);
      });
    },

    hasDateFilter() {
      return this.startDate || this.endDate;
    },
    
    // History-related computed properties
    paginatedHistory() {
      // API handles pagination, so return the summaryHistory directly
      return this.summaryHistory;
    },
    
    totalHistoryPages() {
      return Math.ceil(this.totalHistoryItems / this.historyPageSize);
    },
    
  },

  methods: {
    updateAIStatus(status) {
      console.log("AI Status update:", status);
      this.aiStatus = status;
    },

    // Modified to display error message in main response area
    handleAIError(error) {
      console.error("AI Error:", error);
      this.errorMessage = error;
      this.isLoading = false;
      this.queryAttempted = true;
    },

    async sendToAICopilot() {
      if (!this.canQuery) {
        console.warn("Cannot query: Missing talent or time range");
        this.errorMessage =
          "Please select both a talent and time range before querying.";
        this.queryAttempted = true;
        return;
      }

      this.isLoading = true;
      this.queryAttempted = true;
      this.aiResponse = null; // Reset previous response
      this.errorMessage = ""; // Clear any previous errors

      // console.log("Selected time range:", this.selectedTimeRange);
      try {
        const context = {
          talent_id: this.selectedTalentId,
          talent_name: this.selectedTalent,
          project_id: this.$route.params.projectId,

          start_date: this.startDate,
          end_date: this.endDate,
          // start_date: this.selectedTimeRange.startDate,
          // end_date: this.selectedTimeRange.endDate,
          user_id: this.$store.getters.userId,
          project_name: this.projectName,
          prompt_template_type: "talent_activity_summary",
        };

        this.$refs.aiSummaryCopilot.sendContext(context);
      } catch (error) {
        console.error("Error preparing data for AI:", error);
        this.errorMessage =
          "Failed to prepare data for AI summary. Please try again.";
        this.isLoading = false;
      }
    },

    handleAIResponse(response) {
      console.log("Received AI response:", response);
      this.aiResponse = this.$refs.aiSummaryCopilot.formatRawResponse(response);
      this.isLoading = false;
      
      // Generate a unique ID for this summary for tracking save status
      this.currentSummaryId = Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    },

    async loadHistory() {
      if (this.isLoadingHistory) return; 
      
      this.isLoadingHistory = true;
      this.historyError = null;

      try {
        const params = {
          page: this.currentHistoryPage,
          page_size: this.historyPageSize,
          ...this.historyFilters, 
        };

        Object.keys(params).forEach(key => {
          if (params[key] === null || params[key] === undefined || params[key] === '') {
            delete params[key];
          }
        });

        const response = await apiClient.get(`/web/ai-summaries/project/${this.$route.params.projectId}`, {
          params
        });

        if (response.data) {
          this.summaryHistory = response.data.summaries || [];
          this.totalHistoryItems = response.data.pagination?.total_count || 0;
          console.log(`Loaded ${this.summaryHistory.length} items from API (page ${this.currentHistoryPage})`);
        } else {
          throw new Error('Invalid response format');
        }
      } catch (error) {
        console.error("Error loading history from API:", error);
        this.historyError = this.handleApiError(error, 'load history');
        this.summaryHistory = [];
        this.totalHistoryItems = 0;
      } finally {
        this.isLoadingHistory = false;
      }
    },

    async deleteHistoryItem(itemId) {
      if (!itemId) return;
      
      if (!confirm("Are you sure you want to delete this summary from history?")) {
        return;
      }

      try {
        const response = await apiClient.delete(`/web/ai-summaries/summary/${itemId}`);
        
        if (response.status === 200 || response.data) {
          console.log("History item deleted:", itemId);
          
          this.loadHistory();
          
          if (this.selectedHistoryItem && this.selectedHistoryItem.id === itemId) {
            this.closeHistoryDetail();
          }
          
          this.successMessage = "Summary deleted successfully";
          setTimeout(() => {
            this.successMessage = "";
          }, 3000);
        }
      } catch (error) {
        console.error("Error deleting history item:", error);
        this.errorMessage = this.handleApiError(error, 'delete summary');
      }
    },

    viewHistoryItem(historyItem) {
      this.selectedHistoryItem = historyItem;
    },

    closeHistoryDetail() {
      this.selectedHistoryItem = null;
    },

    getPreviewText(historyItem) {
      if (!historyItem) return "";
      
      if (historyItem.status === 'completed' && historyItem.has_response) {
        return "Summary completed successfully. Click to view full details.";
      } else if (historyItem.status === 'processing') {
        return "Summary is being processed...";
      } else if (historyItem.status === 'failed') {
        const errorMsg = historyItem.error_message || "Unknown error occurred";
        return `Failed to generate summary: ${errorMsg}`;
      } else {
        return `Status: ${historyItem.status || 'Unknown'}`;
      }
    },

    formatHistoryDate(historyItem) {
      if (!historyItem) return "";
      
      const startDate = historyItem.start_date;
      const endDate = historyItem.end_date;
      
      if (!startDate || !endDate) return "Date not available";
      
      const start = new Date(startDate);
      const end = new Date(endDate);
      return `${start.toLocaleDateString()} - ${end.toLocaleDateString()}`;
    },

    formatTimestamp(timestamp) {
      if (!timestamp) return "";
      
      const date = new Date(timestamp);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 60) {
        return diffMins === 0 ? 'Just now' : `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
      } else if (diffHours < 24) {
        return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
      } else if (diffDays < 7) {
        return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
      } else {
        return date.toLocaleDateString();
      }
    },

    getTalentNameFromHistoryItem(historyItem) {
      return historyItem.talent_name || 'Unknown Talent';
    },

    getTalentInitialsFromHistoryItem(historyItem) {
      const name = historyItem.talent_name || '';
      const nameParts = name.split(' ');
      
      const firstInitial = nameParts[0] ? nameParts[0].charAt(0).toUpperCase() : '';
      const lastInitial = nameParts[1] ? nameParts[1].charAt(0).toUpperCase() : '';
      
      return `${firstInitial}${lastInitial}` || 'TT';
    },

    handleApiError(error, action = 'perform action') {
      let errorMessage = `Failed to ${action}`;
      
      if (error.response) {
        const status = error.response.status;
        const data = error.response.data;
        
        switch (status) {
          case 400:
            errorMessage = `Bad request: ${data.message || 'Invalid data provided'}`;
            break;
          case 401:
            errorMessage = 'Authentication required. Please log in again.';
            break;
          case 403:
            errorMessage = 'Access denied. You do not have permission to perform this action.';
            break;
          case 404:
            errorMessage = 'Resource not found. The requested data may have been deleted.';
            break;
          case 500:
            errorMessage = 'Server error. Please try again later.';
            break;
          default:
            errorMessage = data.message || `Server error (${status}). Please try again.`;
        }
      } else if (error.request) {
        // Network error
        errorMessage = 'Network error. Please check your connection and try again.';
      } else {
        // Other error
        errorMessage = error.message || `Unknown error occurred while trying to ${action}`;
      }
      
      return errorMessage;
    },

    // handleTimeRange(timeRange) {
    //   console.log("Received time range:", timeRange);

    //   // Parse the dates from the received format
    //   if (timeRange.start_date && timeRange.end_date) {
    //     const startDate = new Date(timeRange.start_date);
    //     const endDate = new Date(timeRange.end_date);

    //     // Format times in HH:MM format
    //     const formatTime = (date) => {
    //       return date.toLocaleTimeString([], {
    //         hour: "2-digit",
    //         minute: "2-digit",
    //       });
    //     };

    //     // Create the properly formatted object that formattedTimeRange expects
    //     this.selectedTimeRange = {
    //       startTime: formatTime(startDate),
    //       endTime: formatTime(endDate),
    //       day: startDate.getDate(),
    //       month: startDate.getMonth() + 1,
    //       year: startDate.getFullYear(),
    //       startTimestamp: Math.floor(startDate.getTime() / 1000),
    //       endTimestamp: Math.floor(endDate.getTime() / 1000),
    //       startDate: startDate.toISOString(),
    //       endDate: endDate.toISOString(),
    //     };
    //     this.saveFiltersToStorage();
    //   } else {
    //     // Handle case where timeRange doesn't have expected properties
    //     this.selectedTimeRange = null;
    //   }
    // },

    selectTalent(talent) {
      this.selectedTalent = `${this.capitalize(
        talent.talent_first_name
      )} ${this.capitalize(talent.talent_last_name)}`;
      this.selectedTalentId = talent.talent_id;
      this.closeModal();

      // Emit to parent component
      this.$emit("update-global-filters", {
        globalSelectedTalent: this.selectedTalent,
        globalSelectedTalentId: this.selectedTalentId,
      });
    },

    getInitials(firstName, lastName) {
      const firstInitial = firstName
        ? firstName.trim().charAt(0).toUpperCase()
        : "";
      const lastInitial = lastName
        ? lastName.trim().charAt(0).toUpperCase()
        : "";
      return `${firstInitial}${lastInitial}`;
    },

    openModal() {
      this.isModalOpen = true;
    },

    closeModal() {
      this.isModalOpen = false;
      this.searchQuery = "";
    },

    capitalize(text) {
      if (!text) return "";
      return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
    },

    stopEventPropagation(event) {
      event.stopPropagation();
    },

    closeDropdown(event) {
      if (this.$refs.dropdown && !this.$refs.dropdown.contains(event.target)) {
        // Close dropdown logic if needed
      }
    },

    async fetchProject() {
      const projectId = this.$route.params.projectId;
      try {
        const response = await apiClient.get(`/web/projects/${projectId}`);
        this.project = response.data.data;
        this.projectName = this.project.project_name;
        console.log("Project fetched:", this.projectName);
      } catch (error) {
        if (error.response && error.response.status === 404) {
          this.$router.push({ name: "NotFound" });
        } else {
          console.error("Error fetching project data:", error);
          this.errorMessage = `Error loading project: ${
            error.response?.data?.message || error.message
          }`;
        }
      }
    },

    async fetchProjectMembers(projectId) {
      try {
        const response = await apiClient.get(
          `/web/projects/${projectId}/project_members/`
        );
        this.members = response.data.data;
        console.log(`Fetched ${this.members.length} project members`);
      } catch (error) {
        console.error("Error fetching members data:", error);
        this.errorMessage = `Error loading team members: ${
          error.response?.data?.message || error.message
        }`;
      }
    },
    containsHTML(str) {
      const htmlRegex = /<\/?[a-z][\s\S]*>/i;
      return htmlRegex.test(str);
    },
    sanitizeHTML(html) {
      return DOMPurify.sanitize(html);
    },
    // Note: Storage methods removed - now using global state from parent component
    applyDateFilter(data) {
      this.startDate = data.startDate;
      this.endDate = data.endDate;

      // Emit to parent component
      this.$emit("update-global-filters", {
        globalStartDate: this.startDate,
        globalEndDate: this.endDate,
      });

      this.sendToAICopilot(); // re-fetch summaries based on new range
    },
    formatDateForDisplay(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
    },
  },

  created() {
    const projectId = this.$route.params.projectId;
    this.fetchProject();
    this.fetchProjectMembers(projectId);
    
    // Note: History will be loaded when user switches to history tab
  },

  mounted() {
    this.$refs.aiSummaryCopilot.connect();
    document.addEventListener("click", this.closeDropdown);

    // For testing purposes only - remove in production
    // if (process.env.NODE_ENV !== 'production') {
    //   this.selectedTimeRange = {
    //     startTime: "11:01",
    //     endTime: "11:02",
    //     day: 7,
    //     month: 3,
    //     year: 2025,
    //     startTimestamp: Math.floor(new Date("2025-03-07T11:01:22Z").getTime() / 1000),
    //     endTimestamp: Math.floor(new Date("2025-03-07T11:02:00Z").getTime() / 1000)
    //   };

    //   this.selectedTalentId = "178";
    //   this.selectedTalent = "Calista Ifenkwe";
    // }
  },

  beforeUnmount() {
    document.removeEventListener("click", this.closeDropdown);
  },
};
</script>

<style scoped>
.smoke-light {
  background-color: rgba(0, 0, 0, 0.5);
}

/* Line clamp utility for text truncation */
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Ensure smooth transitions for view switching */
.transition-colors {
  transition-property: color, background-color, border-color;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 150ms;
}

/* Custom scrollbar for history modal */
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>
